import { z } from 'zod'

import type { FeatureFlagKeyType } from './feature-flags'

// =====================
// Engagement (work accounts): streaks, achievements, weekly goals
// =====================
//
// Work-account only, behind the GAMIFICATION feature flag (OFF in
// PERSONAL_ACCOUNT_FLAG_PROFILE). Personal spaces keep the family stars/chore
// streak loop in `./stars`; nothing here is ever written for a PERSONAL org.
//
// Signals are recorded server-side only. An "active day" is any day (in the
// organization's timezone) with at least one signal by the user.

/** Server-side signals that make a day active. */
export const ENGAGEMENT_SIGNAL_TYPES = [
  'TASK_COMPLETED',
  'TIME_LOGGED',
  'TICKET_RESOLVED',
  'DEAL_ADVANCED',
  'DEAL_WON',
  'FOCUS_PLAN_DONE',
] as const
export type EngagementSignalType = (typeof ENGAGEMENT_SIGNAL_TYPES)[number]

/** What a weekly goal can measure. */
export const ENGAGEMENT_METRICS = [
  'TASKS_COMPLETED',
  'HOURS_LOGGED',
  'TICKETS_RESOLVED',
  'DEALS_ADVANCED',
  'FOCUS_PLANS_DONE',
  'STREAK_KEPT',
] as const
export type EngagementMetric = (typeof ENGAGEMENT_METRICS)[number]

export interface EngagementMetricMeta {
  metric: EngagementMetric
  /** Sentence-case label for pickers ("Tasks completed"). */
  label: string
  /** Short noun used after a number ("12 tasks"). */
  noun: string
  unit: 'count' | 'hours' | 'days'
  /** Badge glyph used to draw the metric. */
  glyph: BadgeGlyphId
  /** Hide the metric when this module is off for the org. */
  featureFlag?: FeatureFlagKeyType
}

/** Ordered for pickers. */
export const ENGAGEMENT_GOAL_METRICS: readonly EngagementMetricMeta[] = [
  {
    metric: 'TASKS_COMPLETED',
    label: 'Tasks completed',
    noun: 'tasks',
    unit: 'count',
    glyph: 'check-stack',
    featureFlag: 'TASKS',
  },
  {
    metric: 'HOURS_LOGGED',
    label: 'Hours logged',
    noun: 'hours',
    unit: 'hours',
    glyph: 'clock',
    featureFlag: 'TIME_TRACKING',
  },
  {
    metric: 'TICKETS_RESOLVED',
    label: 'Tickets resolved',
    noun: 'tickets',
    unit: 'count',
    glyph: 'shield',
    featureFlag: 'TICKETS',
  },
  {
    metric: 'DEALS_ADVANCED',
    label: 'Deals moved forward',
    noun: 'deals',
    unit: 'count',
    glyph: 'rocket',
    featureFlag: 'DEALS',
  },
  {
    metric: 'FOCUS_PLANS_DONE',
    label: 'Day plans finished',
    noun: 'plans',
    unit: 'count',
    glyph: 'target',
    featureFlag: 'FOCUS_MODE',
  },
  {
    metric: 'STREAK_KEPT',
    label: 'Active working days',
    noun: 'days',
    unit: 'days',
    glyph: 'flame',
  },
]

export function getEngagementMetricMeta(
  metric: EngagementMetric,
): EngagementMetricMeta {
  return (
    ENGAGEMENT_GOAL_METRICS.find(m => m.metric === metric) ??
    ENGAGEMENT_GOAL_METRICS[0]
  )
}

/**
 * "1 task", "3 tasks", "1 hour", "2.5 hours". Every metric noun is a regular
 * plural, so the singular drops the trailing "s".
 */
export function formatMetricAmount(
  metric: EngagementMetric,
  amount: number,
): string {
  const { noun } = getEngagementMetricMeta(metric)
  const value = Math.round(amount * 10) / 10
  return `${value} ${value === 1 ? noun.replace(/s$/, '') : noun}`
}

/** Streak lengths that unlock a streak badge (work accounts). */
export const WORK_STREAK_MILESTONES = [3, 7, 14, 30, 100] as const

export type GoalStatus = 'ACTIVE' | 'ACHIEVED' | 'MISSED'
export type GoalSource = 'USER' | 'SUGGESTED'

export type AchievementTier = 'bronze' | 'silver' | 'gold'
export type AchievementCategory =
  | 'onboarding'
  | 'tasks'
  | 'streaks'
  | 'time'
  | 'tickets'
  | 'deals'
  | 'focus'
  | 'goals'
  | 'rhythm'

export const BADGE_GLYPH_IDS = [
  'flame',
  'flame-crown',
  'check-single',
  'check-stack',
  'layers',
  'mountain',
  'clock',
  'clock-stack',
  'shield',
  'inbox-zero',
  'handshake',
  'rocket',
  'trophy',
  'target',
  'compass',
  'flag',
  'medal',
  'sunrise',
  'boomerang',
] as const
export type BadgeGlyphId = (typeof BADGE_GLYPH_IDS)[number]

export type CelebrationLevel = 'full' | 'quiet' | 'off'

export interface EngagementPreferences {
  /** Let teammates see your badges on your member profile. Off by default. */
  showBadgesOnProfile: boolean
  /** 0=Sun..6=Sat. Days off never break a streak. */
  workingDays: number[]
  /** full = unlock moment, quiet = toast only, off = nothing. */
  celebrationLevel: CelebrationLevel
}

export const DEFAULT_ENGAGEMENT_PREFERENCES: EngagementPreferences = {
  showBadgesOnProfile: false,
  workingDays: [1, 2, 3, 4, 5],
  celebrationLevel: 'full',
}

export interface EngagementStreak {
  /** Consecutive active working days, today given grace until midnight. */
  current: number
  longest: number
  /** Today is a working day with no activity yet and a streak is running. */
  atRiskToday: boolean
  /** Org-local YYYY-MM-DD. */
  today: string
  workingDays: number[]
}

export interface EngagementWeek {
  /** Monday, org-local YYYY-MM-DD. */
  weekStart: string
  weekEnd: string
  /** Active org-local dates inside this week. */
  activeDates: string[]
  tasksCompleted: number
  ticketsResolved: number
  dealsAdvanced: number
  focusPlansDone: number
  hoursLogged: number
}

export interface EngagementGoal {
  id: string
  metric: EngagementMetric
  /** In the metric's unit (hours for HOURS_LOGGED). */
  target: number
  progress: number
  status: GoalStatus
  source: GoalSource
  weekStart: string
  achievedAt: string | null
}

/** An unlock the client has not celebrated yet. */
export interface EngagementUnlock {
  id: string
  kind: 'achievement' | 'goal'
  /** Achievement key, or the goal metric. */
  key: string
  earnedAt: string
  tier?: AchievementTier
  /** Goal target, or achievement context (e.g. `{ streak: 7 }`). */
  context?: Record<string, unknown> | null
}

export interface EngagementMeResponse {
  streak: EngagementStreak
  week: EngagementWeek
  goals: EngagementGoal[]
  achievements: {
    earnedCount: number
    totalCount: number
    recent: { key: string; earnedAt: string }[]
  }
  unseen: EngagementUnlock[]
  preferences: EngagementPreferences
}

export interface AchievementWithState {
  key: string
  name: string
  description: string
  tier: AchievementTier
  category: AchievementCategory
  glyph: BadgeGlyphId
  earnedAt: string | null
  progress: { current: number; target: number } | null
}

export interface GoalSuggestion {
  metric: EngagementMetric
  suggestedTarget: number
  /** Mean of the trailing full weeks, in the metric's unit. */
  average: number
  /** Trailing full weeks, oldest first. */
  weeks: number[]
  /** Hard ceiling for the metric (weekly capacity for hours), if any. */
  cap: number | null
}

export interface MemberBadgesResponse {
  shared: boolean
  badges: { key: string; earnedAt: string }[]
}

// ---- DTOs ----

export const CreateGoalSchema = z.object({
  metric: z.enum(ENGAGEMENT_METRICS),
  target: z.number().int().min(1).max(10000),
  source: z.enum(['USER', 'SUGGESTED']).optional(),
})
export type CreateGoalDto = z.infer<typeof CreateGoalSchema>

export const UpdateGoalSchema = z.object({
  target: z.number().int().min(1).max(10000),
})
export type UpdateGoalDto = z.infer<typeof UpdateGoalSchema>

export const AckUnlocksSchema = z.object({
  achievementIds: z.array(z.string().uuid()).max(200).optional(),
  goalIds: z.array(z.string().uuid()).max(200).optional(),
  all: z.boolean().optional(),
})
export type AckUnlocksDto = z.infer<typeof AckUnlocksSchema>

export const EngagementPreferencesSchema = z.object({
  showBadgesOnProfile: z.boolean(),
  workingDays: z
    .array(z.number().int().min(0).max(6))
    .min(1)
    .max(7)
    .transform(days => Array.from(new Set(days)).sort((a, b) => a - b)),
  celebrationLevel: z.enum(['full', 'quiet', 'off']),
})
export const UpdateEngagementPreferencesSchema =
  EngagementPreferencesSchema.partial()
export type UpdateEngagementPreferencesDto = z.input<
  typeof UpdateEngagementPreferencesSchema
>

/** Parse a stored preference payload, falling back to defaults per field. */
export function resolveEngagementPreferences(
  raw: unknown,
): EngagementPreferences {
  const source =
    raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const shape = EngagementPreferencesSchema.shape
  const field = <K extends keyof EngagementPreferences>(
    key: K,
  ): EngagementPreferences[K] => {
    const parsed = shape[key].safeParse(source[key])
    return parsed.success
      ? (parsed.data as EngagementPreferences[K])
      : DEFAULT_ENGAGEMENT_PREFERENCES[key]
  }
  return {
    showBadgesOnProfile: field('showBadgesOnProfile'),
    workingDays: field('workingDays'),
    celebrationLevel: field('celebrationLevel'),
  }
}
